import { getRoute, apiSend } from "../axios";

/**
 * Generic API wrapper using getRoute + apiSend
 * Generic API wrapper using getRoute + apiSend
 * Generic API wrapper using getRoute + apiSend
 */
const fetchApiData = async ({
  endpoint,
  method = "get",
  params = {},
  validateSuccess = false,
  throwOnError = false,
  headers = {},
}) => {
  try {
    const url = getRoute(endpoint);
    const response = await apiSend(url, method, params);

    if (validateSuccess && !response?.data?.success)
    if (validateSuccess && !response?.data?.success)
      throw new Error(`API request failed for ${endpoint}`);

    return response?.data?.result;
    return response?.data?.result;
  } catch (error) {
    console.error(`Error fetching data from ${endpoint}:`, error);
    if (throwOnError) throw error;
    return null;
  }
};

// API Endpoint Configurations
const API_ENDPOINTS = {
  countries: { endpoint: "countries" },
  citiesByCountry: { endpoint: "countries" },
  currencies: { endpoint: "currencies" },
  policies: { endpoint: "cancelation_policy" },
  productTypes: { endpoint: "AUTOCOMPLETE_product_types" },
  activityTypes: { endpoint: "AUTOCOMPLETE_activity_types" },
  mealTimes: { endpoint: "meal_times" },
  mealCategories: { endpoint: "meal_categories" },
  transportTypes: { endpoint: "AUTOCOMPLETE_TERMINAL_TYPE" },
  arrivalTimes: { endpoint: "arrival_times" },
  keywordsActivity: { endpoint: "AUTOCOMPLETE_KEYWORDS_ACTIVITY" },
  facilities: { endpoint: "AUTOCOMPLETE_CAR_EQUIPMENTS" },
  transferTypes: { endpoint: "AUTOCOMPLETE_transfer_types" },
  equipments: { endpoint: "equipments" },
  terminalTypes: { endpoint: "AUTOCOMPLETE_terminal_types" },
  airports: { endpoint: "airports" },
  hotels: { endpoint: "hotels" },
  terminalsByType: { endpoint: "terminals_by_type" },
  transferKeywords: { endpoint: "keywords_transfer" },
  vehiclesByProduct: { endpoint: "vehicles_by_product" },

  //hotel
  hotelFacilities: { endpoint: "AUTOCOMPLETE_hotel_facilities" },
  mealTypes: { endpoint: "AUTOCOMPLETE_hotel_meal_types" },
  bedTypes: { endpoint: "AUTOCOMPLETE_hotel_bed_types" },
  roomOptions: { endpoint: "AUTOCOMPLETE_hotel_room_option" },
  roomTypes: { endpoint: "AUTOCOMPLETE_hotel_room_types" },

  //flight
  flightTypes: { endpoint: "flight_types" }, // classes, vol, route
  flightCabins: { endpoint: "flight_cabins" }, // cabin classes
  flightAirlines: { endpoint: "flight_airlines" }, // airline companies
  flightMealTypes: { endpoint: "flight_meal_types" }, // meal types
  flightEntertainmentTypes: { endpoint: "flight_entertainment_types" }, // entertainment types
};

// Dynamic function creator
const createApiFunction =
  (config) =>
  async (pathParam = null, additionalParams = {}) => {
    let endpoint = config.endpoint;
    if (pathParam && endpoint.includes("{")) {
      const paramName = endpoint.match(/\{(\w+)\}/)?.[1];
      if (paramName) endpoint = endpoint.replace(`{${paramName}}`, pathParam);
    }

    return fetchApiData({
      endpoint,
      params: additionalParams,
      validateSuccess: config.validateSuccess || false,
      throwOnError: config.throwOnError || false,
      headers: config.headers || {},
    });
  };

// Simple endpoints (direct mapping)
const simpleEndpoints = [
  "countries",
  "currencies",
  "policies",
  "productTypes",
  "activityTypes",
  "mealTimes",
  "mealCategories",
  "transportTypes",
  "arrivalTimes",
  "facilities",
  "transferTypes",
  "equipments",
  "terminalTypes",
  "transferKeywords",
  "hotelFacilities",
  "mealTypes",
  "bedTypes",
  "roomOptions",
  "roomTypes",
  "flightTypes",
  "flightCabins",
  "flightAirlines",
  "flightMealTypes",
  "flightEntertainmentTypes",
];

const api = simpleEndpoints.reduce(
  (acc, key) => ({
    ...acc,
    [`fetch${key.charAt(0).toUpperCase() + key.slice(1)}`]: createApiFunction(
      API_ENDPOINTS[key]
    ),
  }),
  {}
);

// Special case endpoints
export const fetchCountries = api.fetchCountries;
export const fetchCurrencies = api.fetchCurrencies;
export const fetchPolicies = api.fetchPolicies;
export const fetchProductTypes = api.fetchProductTypes;
export const fetchActivityTypes = api.fetchActivityTypes;
export const fetchMealTimes = api.fetchMealTimes;
export const fetchMealCategories = api.fetchMealCategories;
export const fetchTransportTypes = api.fetchTransportTypes;
export const fetchArrivalTimes = api.fetchArrivalTimes;
export const fetchFacilities = api.fetchFacilities;
export const fetchTransferTypes = api.fetchTransferTypes;
export const fetchEquipments = api.fetchEquipments;
export const fetchTerminalTypes = api.fetchTerminalTypes;
export const fetchTransferKeywords = api.fetchTransferKeywords;
export const fetchHotelFacilities = api.fetchHotelFacilities;
export const fetchMealTypes = api.fetchMealTypes;
export const fetchBedTypes = api.fetchBedTypes;
export const fetchRoomOptions = api.fetchRoomOptions;
export const fetchRoomTypes = api.fetchRoomTypes;
export const fetchFlightTypes = api.fetchFlightTypes;
export const fetchFlightCabins = api.fetchFlightCabins;
export const fetchFlightAirlines = api.fetchFlightAirlines;
export const fetchFlightMealTypes = api.fetchFlightMealTypes;
export const fetchFlightEntertainmentTypes = api.fetchFlightEntertainmentTypes;

export const fetchCitiesByCountry = async (countryId, params = {}) => {
  if (!countryId) {
    throw new Error("Country ID is required");
  }

  try {
    const url = getRoute("cities") + "/" + countryId;
    const response = await apiSend(url, params, "get");

    return response.data;
  } catch (error) {
    console.error(`Error fetching cities for country ${countryId}:`, error);
    return null;
  }
};

export const fetchKeywords = async (productType = "activity", search = "") => {
  const endpoint =
    productType === "Transport"
      ? API_ENDPOINTS.transferKeywords.endpoint
      : API_ENDPOINTS.keywords.endpoint;
  return fetchApiData({ endpoint, params: { search } });
};

export const fetchAirports = (search = "") =>
  fetchApiData({
    endpoint: API_ENDPOINTS.airports.endpoint,
    params: { q: search },
  });

export const fetchAirlines = (search = "") =>
  fetchApiData({
    endpoint: API_ENDPOINTS.flightAirlines.endpoint,
    params: { q: search },
  });

export const fetchHotels = (search = "") =>
  fetchApiData({
    endpoint: API_ENDPOINTS.hotels.endpoint,
    params: { q: search },
  });

export const fetchTerminalsByType = (typeId, search = "") => {
  if (!typeId) throw new Error("Type ID is required");

  const handlers = {
    airport: () => fetchAirports(search),
    hotel: () => fetchHotels(search),
    default: () =>
      fetchApiData({
        endpoint: API_ENDPOINTS.terminalsByType.endpoint,
        params: { type_id: typeId, search },
      }),
  };

  return (handlers[typeId] || handlers.default)();
};

export const fetchVehiclesByProduct = (productId) => {
  if (!productId) throw new Error("Product ID is required");
  return createApiFunction(API_ENDPOINTS.vehiclesByProduct)(productId);
};

// Batch operations
const createBatchFetcher = (keys) => async () => {
  try {
    const results = await Promise.all(
      keys.map((key) => createApiFunction(API_ENDPOINTS[key])())
    );
    return keys.reduce((acc, key, i) => ({ ...acc, [key]: results[i] }), {});
  } catch (error) {
    console.error("Error in batch fetch:", error);
    return null;
  }
};
